import { camelize } from 'casing'
import { toUpper } from 'lodash'
import {
  getErrorMessages,
  isLoading,
  LoadingTypes,
  startLoading,
  stopLoading,
  updateAppAlert,
} from '@labrav/common'

import {
  fetchResellerAgreementData,
  syncAWSResellerAgreementDataToDb,
} from '../../api/marketplace/index'
import type { AppDispatch, RootState } from '../../store/types'

export enum ReceivedResellerAuthorizationActionTypes {
  SET_ACTIVE_RECEIVED_RESALE_AUTHORIZATIONS_DATA = 'SET_ACTIVE_RECEIVED_RESALE_AUTHORIZATIONS_DATA',
  SET_SELECTED_ACTIVE_RECEIVED_RESALE_AUTHORIZATIONS_DATA = 'SET_SELECTED_ACTIVE_RECEIVED_RESALE_AUTHORIZATIONS_DATA',
  CLEAR_ACTIVE_RECEIVED_RESELLER_AUTHORIZATION = 'CLEAR_ACTIVE_RECEIVED_RESELLER_AUTHORIZATION',
}

type ReceivedResellerAgreementItem = {
  discount_type?: string
  received_reseller_agreement_dimensions?: unknown
  [key: string]: unknown
}

export const setActiveReceivedResaleAuthorizations = (
  agreements: unknown[]
) => ({
  type: ReceivedResellerAuthorizationActionTypes.SET_ACTIVE_RECEIVED_RESALE_AUTHORIZATIONS_DATA,
  payload: { agreements },
})

export const clearActiveReceivedResaleAuthorization = () => ({
  type: ReceivedResellerAuthorizationActionTypes.CLEAR_ACTIVE_RECEIVED_RESELLER_AUTHORIZATION,
})

export const transformReceivedResellerAgreements = (
  data: ReceivedResellerAgreementItem[]
) => {
  return (data || []).map(item => {
    const {
      discount_type,
      received_reseller_agreement_dimensions,
      ...otherItem
    } = item
    return {
      ...otherItem,
      dimensions: received_reseller_agreement_dimensions,
      discount_type: discount_type ? toUpper(discount_type) : '',
    }
  })
}

const processReceivedResellerAgreementsForCP = async ({
  partnerId,
  crmOpportunityId,
  status,
  cloudProductUid,
}: {
  partnerId: string
  crmOpportunityId?: string
  status?: string
  cloudProductUid?: string
}) => {
  const receivedResaleAuthsInDbResponse = await fetchResellerAgreementData({
    partnerId,
    sortBy: 'created_at',
    sortOrder: 'desc',
    crmOpportunityId,
    status: status || '',
    origin: 'RECEIVED',
    cloudProductUid,
  })
  if (receivedResaleAuthsInDbResponse.data) {
    return receivedResaleAuthsInDbResponse
  }
  return syncAWSResellerAgreementDataToDb(partnerId, 'RECEIVED')
}

const requestFailureMessage = 'Request failed. Please try again.'

export const getActiveReceivedResaleAuthorizations =
  ({
    partnerId,
    crmOpportunityId,
    subscriptionId,
  }: {
    partnerId: string
    crmOpportunityId?: string
    subscriptionId?: string
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const globalState = getState() as RootState & {
      loading?: unknown
      receivedResellerAuthorization?: {
        activeReceivedResellerAuthorizations?: unknown[]
      }
      subscriptions?: {
        subscriptions?: Array<{
          subscriptionId?: string
          awsProductUid?: string
        }>
      }
    }
    try {
      if (
        isLoading(globalState.loading as never)(
          LoadingTypes.ACTIVE_RESELLER_AUTHORIZATION
        )
      ) {
        return
      }
      const existingActive =
        globalState.receivedResellerAuthorization
          ?.activeReceivedResellerAuthorizations
      if (Array.isArray(existingActive) && existingActive.length > 0) {
        return
      }
    } catch {
      // ignore guard errors
    }
    await dispatch(startLoading(LoadingTypes.ACTIVE_RESELLER_AUTHORIZATION))
    try {
      const subscriptionDetail = globalState.subscriptions?.subscriptions?.find(
        item => item?.subscriptionId === subscriptionId
      )
      const cloudProductUid = subscriptionDetail?.awsProductUid
      const { data } = await processReceivedResellerAgreementsForCP({
        partnerId,
        status: 'active',
        crmOpportunityId,
        cloudProductUid,
      })
      if (!data || (data as { message?: string }).message) {
        throw new Error('No data')
      }
      await dispatch(
        setActiveReceivedResaleAuthorizations(
          camelize(
            transformReceivedResellerAgreements(
              (
                data as {
                  aws_reseller_agreements?: ReceivedResellerAgreementItem[]
                }
              )?.aws_reseller_agreements || []
            )
          )
        )
      )
    } catch (error: unknown) {
      const axiosError = error as { response?: unknown }
      dispatch(
        updateAppAlert({
          message: getErrorMessages([requestFailureMessage])(
            axiosError?.response as never
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.ACTIVE_RESELLER_AUTHORIZATION))
    }
  }
